import React, { useState } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    IconButton,
    Link,
    List,
    ListItem,
    ListItemIcon,
} from '@mui/material';
import { useAppDispatch } from 'app/hooks';
import { ActionsPopper } from 'common/components/ActionsPopper';
import { CopyToClipboard } from 'common/components/CopyToClipboard';
import { setCurrentEntityType, setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { openDrawer, setCurrentDrawerId, setCurrentEntity, setCurrentOperation } from 'common/components/EditDrawer/editDrawerSlice';
import { Roles } from 'common/constants/roles';
import { useAuthContext } from 'common/contexts/AuthContext';
import { UserForeignKey } from 'common/types/Entities';
import { DrawerEntitiesNames,Operations } from 'common/types/TypeDefs';
import { disableIfMissingAnyRole } from 'common/utils/DisableButtonEvaluator';
import { DrawerIDs } from 'common/utils/DrawerIDs';

import { UserProfileForeignSystemsDeleteDialog } from '../UserProfileForeignSystemsDeleteDialog';

import { UserForeignSystemDrawer } from './UserForeignSystemDrawer';
import { UserForeignSystemListTitle } from './UserForeignSystemListTitle';

type UserForeignSystemListProps = {
    data: UserForeignKey[];
};

export function UserForeignSystemList({ data }: Readonly<UserForeignSystemListProps> ) {
    const OASYS_PROD_URL = 'https://oasys.opisnet.com';
    const oasysUrl = import.meta.env.VITE_OASYS_URL ?? OASYS_PROD_URL;

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [editingRow, setEditingRow] = useState<UserForeignKey | undefined>();
    const dispatch = useAppDispatch();
    const { currentUserRoles } = useAuthContext();
    const allowedEditDeleteRoles = [ Roles.Admin, Roles.UserManager ];
    const shouldDisableEditDeleteButton = disableIfMissingAnyRole(currentUserRoles, allowedEditDeleteRoles);

    const handleOpenActionsMenu = (row: UserForeignKey) => (event: React.MouseEvent<HTMLElement>) => {
        if (
            row.foreignKey === editingRow?.foreignKey &&
            row.foreignSystemId === editingRow?.foreignSystemId &&
            anchorEl
        ) {
            handleClosePopper();
        } else {
            setEditingRow(row);
            setAnchorEl(event.currentTarget);
        }
        event.stopPropagation();
    };

    const handleClosePopper = () => {
        setEditingRow(undefined);
        setAnchorEl(null);
    };

    const handleEditButtonClick = () => {
        dispatch(setCurrentOperation(Operations.Edit));
        dispatch(setCurrentEntity(DrawerEntitiesNames.UserForeignSystems));
        dispatch(setCurrentDrawerId(DrawerIDs.UserProfileForeignSystems));
        dispatch(openDrawer());
        setAnchorEl(null);
    };

    const handleDeleteButtonClick = () => {
        dispatch(setDialogOpen(true));
        dispatch(setCurrentEntityType(DrawerEntitiesNames.UserForeignSystems));
        setAnchorEl(null);
    };

    const LIST_WRAPPER_INLINE_STYLES = {
        display: 'flex',
        flexDirection: 'column',
        marginLeft: '24px',
        marginRight: '24px',
    };
    return (
        <List sx={LIST_WRAPPER_INLINE_STYLES}>
            {data.map(row => {
                return (
                    <React.Fragment key={`${row.foreignKey}.${row.userId}`}>
                        <UserForeignSystemListTitle
                            title={row.foreignSystemName}
                        />
                        <ListItem
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    onClick={handleOpenActionsMenu(row)}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            }
                            disablePadding
                        >
                            <ListItemIcon sx={{ minWidth: '0px' }}>
                                <CopyToClipboard
                                    content={row.foreignKey}
                                />
                            </ListItemIcon>
                            <Link
                                style={{ margin: '4px 0' }}
                                href={`${oasysUrl}/Person/Details/${row.foreignKey}`}
                                target={'_blank'}
                            >
                                {row.foreignKey}
                            </Link>
                        </ListItem>
                    </React.Fragment>
                );
            })}
            <UserForeignSystemDrawer
                editingRow={editingRow}
            />
            <UserProfileForeignSystemsDeleteDialog
                editingRow={editingRow}
                title={'Foreign System'}
            />
            <ActionsPopper
                anchorEl={anchorEl}
                handleClosePopper={handleClosePopper}
                actions={[
                    { operation: Operations.Edit, handleOperation: handleEditButtonClick, disabled: shouldDisableEditDeleteButton },
                    { operation: Operations.Delete, handleOperation: handleDeleteButtonClick, disabled: shouldDisableEditDeleteButton },
                ]}
            />
        </List>
    );
}
